import { useState, useRef, useEffect } from "react";
import {
  Filter,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  GripVertical,
} from "lucide-react";
import {
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../../components/ui/table";
import { ConfiguredStateBadge } from "../../../../components/audit/ConfiguredStateBadge";
import { ControlWithUser } from "../../../../models/audit/control";
import { cn } from "../../../../lib/utils";
import { LoadingSpinner } from "../../../../components/ui/loadingSpinner/loadingSpinner";
import { SystemOfRecordBadge } from "../../../../components/SystemOfRecordBadge";
import { useAuth } from "../../../../contexts/authContext";
import { getExternalControlUrl } from "../../../../lib/systemOfRecord";
import { ControlsWelcomeState } from "./ControlsWelcomeState";
import { ImportingControlRow } from "./ImportingControlRow";
import { ControlsColumnWidths } from "./controlsTableColumns";
import { OptroControlSummary } from "../../../../models/audit/optroSync";

interface ControlsTableProps {
  controls: ControlWithUser[];
  // Optro controls still importing — placeholder rows until each lands.
  importingControls: OptroControlSummary[];
  selectedControl: ControlWithUser | null;
  onControlClick: (control: ControlWithUser) => void;
  hasActiveFilters: boolean;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  onSort?: (field: string) => void;
  loading?: boolean;
}

export const ControlsTable = ({
  controls,
  importingControls,
  selectedControl,
  onControlClick,
  hasActiveFilters,
  sortBy,
  sortOrder,
  onSort,
  loading = false,
}: ControlsTableProps) => {
  const { workspace } = useAuth();
  const [columnWidths, setColumnWidths] = useState<ControlsColumnWidths>({
    name: 300,
    process: 200,
    cycle: 200,
    owner: 200,
    state: 200,
  });

  const [resizingColumn, setResizingColumn] = useState<
    keyof ControlsColumnWidths | null
  >(null);
  const startXRef = useRef<number>(0);
  const startWidthRef = useRef<number>(0);

  useEffect(() => {
    if (!resizingColumn) return;

    const handleMouseMove = (e: MouseEvent) => {
      const diff = e.clientX - startXRef.current;
      const newWidth = Math.max(100, startWidthRef.current + diff);
      setColumnWidths((prev) => ({
        ...prev,
        [resizingColumn]: newWidth,
      }));
    };

    const handleMouseUp = () => {
      setResizingColumn(null);
    };

    // Prevent text selection while resizing
    document.body.style.userSelect = "none";
    document.body.style.cursor = "col-resize";

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      document.body.style.userSelect = "";
      document.body.style.cursor = "";
    };
  }, [resizingColumn]);

  const handleResizeStart = (
    e: React.MouseEvent,
    column: keyof ControlsColumnWidths,
  ) => {
    e.preventDefault();
    e.stopPropagation();
    setResizingColumn(column);
    startXRef.current = e.clientX;
    startWidthRef.current = columnWidths[column];
  };

  const SortableHeader = ({
    field,
    children,
    className,
  }: {
    field: keyof ControlsColumnWidths;
    children: React.ReactNode;
    className?: string;
  }) => {
    const isSorted = sortBy === field;
    const icon = isSorted
      ? sortOrder === "asc"
        ? ArrowUp
        : ArrowDown
      : ArrowUpDown;
    const Icon = icon;

    return (
      <TableHead
        className={cn(
          "cursor-pointer select-none hover:bg-stone-100 transition-colors relative border-r group sticky top-0 bg-stone-50 z-20 border-y",
          className,
        )}
        style={{
          width: columnWidths[field],
          minWidth: columnWidths[field],
        }}
        onClick={() => onSort?.(field)}
      >
        <div className="flex items-center gap-2 pr-2">
          {children}
          <Icon
            className={cn("h-4 w-4", isSorted ? "opacity-100" : "opacity-50")}
          />
        </div>
        <div
          className="absolute right-0 top-0 bottom-0 w-4 cursor-col-resize flex items-center justify-center z-10"
          onMouseDown={(e) => handleResizeStart(e, field)}
          onClick={(e) => e.stopPropagation()}
        >
          <GripVertical className="h-4 w-4 text-stone-400 opacity-0 group-hover:opacity-100 transition-opacity" />
        </div>
      </TableHead>
    );
  };

  // With no controls at all (and nothing filtered away), skip the table
  // chrome entirely and show the welcome entry cards on their own. A first
  // import in flight shows the table with its placeholder rows instead.
  const hasRows = controls.length > 0 || importingControls.length > 0;
  if (!loading && !hasRows && !hasActiveFilters) {
    return <ControlsWelcomeState />;
  }

  return (
    <table
      className={cn(
        "w-full caption-bottom text-sm border-separate border-spacing-0",
      )}
      style={{ tableLayout: "fixed" }}
    >
      <TableHeader>
        <TableRow>
          <SortableHeader field="name" className="border-l">
            CONTROL NAME
          </SortableHeader>
          <SortableHeader field="process">PROCESS</SortableHeader>
          <SortableHeader field="cycle">CYCLE</SortableHeader>
          <SortableHeader field="owner">OWNER</SortableHeader>
          <SortableHeader field="state" className="border-r">
            STATE
          </SortableHeader>
        </TableRow>
      </TableHeader>
      <TableBody>
        {loading && !hasRows ? (
          <TableRow>
            <TableCell colSpan={5} className="text-center py-12 border-x">
              <div className="flex items-center justify-center">
                <LoadingSpinner />
              </div>
            </TableCell>
          </TableRow>
        ) : !hasRows ? (
          <TableRow>
            <TableCell colSpan={5} className="text-center py-12 border-x">
              <div className="text-stone-500">
                <Filter className="mx-auto h-12 w-12 mb-4 opacity-50" />
                <p className="text-lg font-medium mb-2">No controls found</p>
                <p className="text-sm">
                  Try adjusting your filters or search terms
                </p>
              </div>
            </TableCell>
          </TableRow>
        ) : (
          [
            ...importingControls.map((importing) => (
              <ImportingControlRow
                key={`importing-${importing.externalRef}`}
                control={importing}
                columnWidths={columnWidths}
              />
            )),
            ...controls.map((control) => {
              const isSelected = selectedControl?.id === control.id;
              return (
                <TableRow
                  key={control.id}
                  role="button"
                  className={`cursor-pointer transition-colors ${
                    isSelected
                      ? "bg-blue-50 hover:bg-blue-50"
                      : "hover:bg-stone-50"
                  }`}
                  onClick={() => onControlClick(control)}
                >
                  <TableCell
                    className="font-medium border-x"
                    style={{
                      width: columnWidths.name,
                      maxWidth: columnWidths.name,
                    }}
                  >
                    <div className="flex items-center gap-2 overflow-hidden">
                      <p className="truncate" title={control.name}>
                        {control.name}
                      </p>
                      {control.externalRef && (
                        <SystemOfRecordBadge
                          externalRef={control.externalRef}
                          controlUrl={
                            workspace?.systemOfRecord
                              ? getExternalControlUrl(
                                  workspace.systemOfRecord,
                                  control.externalRef.externalRef,
                                )
                              : null
                          }
                          iconOnly
                        />
                      )}
                    </div>
                  </TableCell>
                  <TableCell
                    className="text-sm text-stone-600 border-r"
                    style={{
                      width: columnWidths.process,
                      maxWidth: columnWidths.process,
                    }}
                  >
                    <div className="truncate">{control.process || "-"}</div>
                  </TableCell>
                  <TableCell
                    className="text-sm text-stone-600 border-r"
                    style={{
                      width: columnWidths.cycle,
                      maxWidth: columnWidths.cycle,
                    }}
                  >
                    <div className="truncate">{control.cycle || "-"}</div>
                  </TableCell>
                  <TableCell
                    className="text-sm text-stone-600 border-r"
                    style={{
                      width: columnWidths.owner,
                      maxWidth: columnWidths.owner,
                    }}
                  >
                    <div className="truncate">
                      {control.controlOwner || "-"}
                    </div>
                  </TableCell>
                  <TableCell
                    className="text-sm text-stone-600 border-r"
                    style={{
                      width: columnWidths.state,
                      maxWidth: columnWidths.state,
                    }}
                  >
                    <ConfiguredStateBadge state={control.configuredState} />
                  </TableCell>
                </TableRow>
              );
            }),
          ]
        )}
      </TableBody>
    </table>
  );
};
